import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-sm text-base font-normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 disabled:opacity-100",
  {
    variants: {
      variant: {
        default: "btn-primary",
        primary:
          "font-medium text-white border bg-primary-blue border-primary-blue hover:bg-primary-blue-dark hover:border-primary-blue-dark hover:text-white focus:text-white focus:bg-primary-blue-dark focus:border-primary-blue-dark focus:outline-none active:bg-primary-blue-dark active:border-primary-blue-dark active:text-white disabled:bg-gray-700 disabled:text-white disabled:border-gray-700",
        "primary-orange":
          "font-medium text-white border bg-primary-orange border-primary-orange hover:bg-primary-orange-dark hover:border-primary-orange-dark hover:text-white focus:bg-primary-orange-dark focus:border-primary-orange-dark focus:outline-none active:bg-primary-orange-dark active:border-primary-orange-dark active:text-white disabled:bg-gray-700 disabled:text-white disabled:border-gray-700",
        secondary:
          "font-medium bg-white border text-primary-blue border-primary-blue hover:text-primary-blue-dark hover:border-primary-blue-dark hover:bg-white focus:ring-accent-blue focus:bg-accent-blue focus:outline-none active:text-primary-blue-dark active:border-primary-blue-dark active:bg-white disabled:bg-white disabled:text-gray-500 disabled:border-gray-700",
        neutral:
          "font-medium bg-white border text-black border-gray-300 hover:text-black hover:border-gray-400 hover:bg-white focus:ring-gray-300 focus:bg-white focus:outline-none active:text-black active:border-gray-400 active:bg-white disabled:bg-white disabled:text-gray-500 disabled:border-gray-300",
        "secondary-orange":
          "font-medium bg-white border text-black border-primary-orange hover:text-black hover:border-primary-orange hover:bg-white focus:ring-accent-orange focus:bg-accent-orange focus:outline-none active:text-black active:border-primary-orange active:bg-white disabled:bg-white disabled:text-gray-500 disabled:border-gray-700",
        danger:
          "font-medium bg-white border text-primary-red border-primary-red hover:text-primary-red-light hover:border-primary-red hover:bg-white focus:text-white focus:ring-primary-red focus:bg-primary-red focus:outline-none active:text-white active:border-primary-red active:bg-primary-red disabled:bg-white disabled:text-primary-red-lighter disabled:border-primary-red-lighter",
        facebook:
          "flex items-center w-full text-white bg-facebook hover:bg-facebook-hover hover:text-white disabled:bg-gray-700 disabled:text-white",
        twitter:
          "flex items-center w-full text-white hover:text-white bg-twitter hover:bg-twitter-hover disabled:bg-gray-700 disabled:text-white",
        linkedin:
          "flex items-center w-full text-white hover:text-white bg-linkedin hover:bg-linkedin-hover disabled:bg-gray-700 disabled:text-white",
        destructive:
          "font-medium text-white border bg-primary-red border-primary-red hover:text-white hover:bg-primary-red-light hover:border-primary-red-light focus:bg-primary-red-light focus:text-white focus:ring-primary-red focus:outline-none active:text-white active:border-primary-red-light active:bg-primary-red-light disabled:bg-primary-red-lighter disabled:text-white disabled:border-primary-red-lighter",
        outline:
          "font-medium bg-white border text-primary-blue border-primary-blue disabled:bg-gray-700 disabled:text-white disabled:border-gray-700 focus:ring-accent-blue focus:bg-accent-blue focus:outline-none disabled:bg-gray-700 disabled:text-white disabled:border-gray-700",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary-blue underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        small: "text-base font-normal px-4 py-2",
        medium: "font-medium text-lg px-6 py-3",
        large: "text-xl font-semibold px-8 py-4",
        sm: "h-8 rounded-sm px-3 text-xs",
        lg: "h-10 rounded-sm px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type = "button", ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        type={type}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
